"use client";

import { useState, useCallback } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import nookies from "nookies";
import { setGuestTokenCookies } from "src/services/api";
import { ORDER_SESSION_COOKIE_MAX_AGE_SECONDS } from "src/utils/order-freshness";
import { guestTokenFromUrl } from "src/utils/guest-token-url-guard";

interface OrderSession {
  orderId: string | null;
  guestToken: string | null;
}

interface UseOrderSessionReturn extends OrderSession {
  setOrderSession: (orderId: string, guestToken: string) => void;
  clearOrderSession: () => void;
}

function getOrderIdFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("orderId");
}

function getGuestTokenFromUrl(): string | null {
  // Including the token the head guard kept for this URL's order
  // (guest-token-url-guard.ts).
  return guestTokenFromUrl(getOrderIdFromUrl());
}

export function useOrderSession(): UseOrderSessionReturn {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [session, setSession] = useState<OrderSession>(() => {
    const cookies = nookies.get(null);
    return {
      orderId: getOrderIdFromUrl() ?? (cookies.orderId || null),
      guestToken: getGuestTokenFromUrl() ?? (cookies.guest_token || null),
    };
  });

  const setOrderSession = useCallback(
    (orderId: string, guestToken: string) => {
      setGuestTokenCookies(guestToken);
      // path:"/" is required, exactly as it is for guest_token. Without it the
      // cookie is scoped to the directory it was written from (e.g.
      // /sk/design/...), so `orderId` is invisible on the home page and at
      // checkout while `guest_token` -- which IS written with path:"/" -- stays
      // readable. That split is the half-session signature behind
      // FOTO-NEXTJS-149 (cookieHadOrderId:false, cookieHadGuestToken:true):
      // OrderProvider's restore needs BOTH, gives up, and starts a new order on
      // top of the customer's existing one.
      // Lifetime derived from CART_MAX_AGE_DAYS (see order-freshness.ts), so
      // this cookie always outlives the cart it points at. No hook currently
      // calls this — it is re-exported from src/hooks/index.ts and unused — but
      // it is public API, and a store that writes a 47h `orderId` next to a
      // 6-day `guest_token` re-creates the half-session split (FOTO-NEXTJS-149)
      // the path:"/" note below exists to prevent.
      nookies.set(null, "orderId", orderId, {
        maxAge: ORDER_SESSION_COOKIE_MAX_AGE_SECONDS,
        path: "/",
      });
      setSession({ orderId, guestToken });

      const params = new URLSearchParams(searchParams?.toString() || "");
      params.set("orderId", orderId);
      params.set("guest_session_token", guestToken);
      router.replace(`${pathname}?${params.toString()}`);
    },
    [router, pathname, searchParams]
  );

  const clearOrderSession = useCallback(() => {
    // Same path rule on the way out: destroy() only removes a cookie whose path
    // matches, so without path:"/" these left the real root-scoped cookies in
    // place and "clearing" the session did nothing.
    nookies.destroy(null, "orderId", { path: "/" });
    nookies.destroy(null, "guest_token", { path: "/" });
    setSession({ orderId: null, guestToken: null });
  }, []);

  return { ...session, setOrderSession, clearOrderSession };
}
